import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MdCleaningServices, MdBolt } from "react-icons/md";
import { HousekeepingRequestDto } from "../utils/housekeepingApi";
import { installAudioUnlock, startAlarm } from "../utils/alertSound";

interface HousekeepingAlertPopupProps {
  alerts: HousekeepingRequestDto[];
  onAcknowledge: () => void;
}

/** Full-attention alert for rooms just sent to cleaning after a check-out. It
 * rings until acknowledged, and cannot be dismissed by clicking beside it. */
const HousekeepingAlertPopup: React.FC<HousekeepingAlertPopupProps> = ({
  alerts,
  onAcknowledge,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    installAudioUnlock();
    return startAlarm();
  }, []);

  const openQueue = () => {
    onAcknowledge();
    navigate("/admin/housekeeping");
  };

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 p-4 animate-overlay-fade-in"
    >
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-4 ring-red-500 animate-modal-pop-in">
        <div className="flex items-center gap-4 bg-red-600 px-6 py-5 text-white animate-pulse">
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/20 text-5xl">
            <MdCleaningServices />
          </span>
          <div>
            <h3 className="text-3xl font-extrabold uppercase tracking-wide">
              Room needs cleaning
            </h3>
            <p className="text-sm text-red-100">
              {alerts.length === 1
                ? "A guest has just checked out"
                : `${alerts.length} guests have just checked out`}
            </p>
          </div>
        </div>

        <div className="max-h-[50vh] space-y-3 overflow-y-auto px-6 py-5">
          {alerts.map((r) => (
            <div
              key={r.housekeepingRequestId}
              className="flex items-center justify-between gap-4 rounded-2xl border-2 border-red-200 bg-red-50 px-5 py-4"
            >
              <div className="min-w-0">
                <p className="text-4xl font-extrabold text-red-700">
                  Room {r.roomNo ?? r.roomId}
                </p>
                <p className="truncate text-sm text-gray-600">
                  {r.roomTypeName ?? ""}
                  {r.guestName ? ` · ${r.guestName}` : ""}
                </p>
              </div>
              <div className="shrink-0 text-right text-sm text-gray-600">
                {r.isUrgent && (
                  <span className="mb-1 inline-flex items-center gap-0.5 rounded bg-orange-100 px-2 py-0.5 text-xs font-semibold uppercase text-orange-700">
                    <MdBolt /> Urgent
                  </span>
                )}
                <p>
                  {new Date(r.checkedOutOn ?? r.createdOn).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <button
            onClick={onAcknowledge}
            className="rounded-xl bg-gray-100 px-5 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
          >
            Acknowledge
          </button>
          <button
            onClick={openQueue}
            className="rounded-xl bg-red-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700"
          >
            Open Housekeeping
          </button>
        </div>
      </div>
    </div>
  );
};

export default HousekeepingAlertPopup;
